/**
 * Lesehilfen des öffentlichen Rahmens (Paket S, 2026-09-10).
 *
 * 1. Kapitelmarkierung: Die Inhaltslinks (`.wa-toc a[href^="#"]`,
 *    `.wa-mobile-toc a[href^="#"]`) und ihre Ziel-IDs bestimmen die Abschnitte.
 *    Der aktive Link erhält `aria-current="location"`. Beim bloßen Scrollen
 *    ändern sich weder URL noch Fokus; ein Klick setzt den Fokus auf die
 *    Zielüberschrift. Ankerversatz (`scroll-margin-top`) und Kopfzeile werden
 *    berücksichtigt; Vorwärts-/Rückwärtsscrollen und das Seitenende auch.
 * 2. Code kopieren/auswählen: Nur auf Knopfdruck wird der unveränderte Text
 *    aus `pre > code` kopiert. Bei verweigertem Clipboard-Zugriff wird er
 *    ausgewählt. Der Umbruch-Schalter ändert ausschließlich die Darstellung.
 * 3. Adaptive Tabelle: `.wa-adaptive-table` mit `button[data-adaptive-toggle]`
 *    wechselt zwischen Vergleichstabelle und beschrifteten Einträgen. Die
 *    Beschriftungen kommen aus `data-label-table` / `data-label-list`.
 * 4. Detail-Inhaltsverzeichnis (`details.wa-detail-toc`) ist auf breiten
 *    Bildschirmen geöffnet, auf schmalen zugeklappt.
 * Geladen von `src/layouts/PublicLayout.astro` (defer). Kein Netzwerk, keine Speicherung.
 */
(() => {
  "use strict";
  const contentsLinks = [...document.querySelectorAll('.wa-toc a[href^="#"], .wa-mobile-toc a[href^="#"]')]
    .map(link => {
      let heading = null;
      try { heading = document.getElementById(decodeURIComponent(link.hash.slice(1))); } catch { /* Ungültiges Fragment. */ }
      return { link, heading };
    }).filter(({ heading }) => heading);
  if (contentsLinks.length) {
    const headings = [...new Set(contentsLinks.map(({ heading }) => heading))]
      .sort((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
    let pending = false, current;
    const updateContents = () => {
      pending = false;
      const headerEdge = document.querySelector('.wa-header')?.getBoundingClientRect().bottom || 0;
      const anchorOffset = parseFloat(getComputedStyle(headings[0]).scrollMarginTop) || 0;
      // Native fragment navigation adds the docked reading bar's scroll padding
      // to the target's margin, even after the main header has scrolled away.
      const scrollPadding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      const readingLine = Math.min(innerHeight - 24, Math.max(24, anchorOffset + scrollPadding + 2, headerEdge + 24));
      let active = headings[0];
      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= readingLine) active = heading;
        else break;
      }
      if (scrollY + innerHeight >= document.documentElement.scrollHeight - 2) active = headings[headings.length - 1];
      if (current === active) return;
      current = active;
      contentsLinks.forEach(({ link, heading }) => {
        if (heading === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };
    const scheduleContents = () => {
      if (!pending) { pending = true; requestAnimationFrame(updateContents); }
    };
    contentsLinks.forEach(({ link, heading }) => link.addEventListener('click', () => {
      if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      scheduleContents();
    }));
    window.addEventListener('scroll', scheduleContents, { passive: true });
    window.addEventListener('resize', scheduleContents);
    window.addEventListener('pageshow', scheduleContents);
    document.addEventListener('toggle', scheduleContents, true);
    document.addEventListener('change', scheduleContents);
    document.fonts?.ready.then(scheduleContents);
    scheduleContents();
  }

  document.querySelectorAll('.wa-code, .wa-code-sample').forEach(figure => {
    const code = figure.querySelector('pre > code');
    const status = figure.querySelector('[data-code-status]');
    if (!code || !status) return;
    const selectCode = () => {
      const selection = window.getSelection();
      if (!selection) return false;
      const range = document.createRange();
      range.selectNodeContents(code);
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    };
    const copy = figure.querySelector('button[data-copy-code]');
    let copying = false;
    copy?.addEventListener('click', async () => {
      if (copying) return;
      copying = true;
      copy.setAttribute('aria-busy', 'true');
      status.textContent = '';
      try {
        await navigator.clipboard.writeText(code.textContent ?? '');
        status.textContent = copy.dataset.copiedLabel;
      } catch {
        status.textContent = selectCode() ? copy.dataset.failedLabel : copy.dataset.manualLabel;
      } finally {
        copying = false;
        copy.removeAttribute('aria-busy');
      }
    });
    const select = figure.querySelector('button[data-select-code]');
    select?.addEventListener('click', () => {
      status.textContent = selectCode() ? select.dataset.selectedLabel : select.dataset.manualLabel;
    });
    const wrap = figure.querySelector('button[data-wrap-code]');
    wrap?.addEventListener('click', () => {
      const enabled = figure.dataset.wrap !== 'true';
      figure.dataset.wrap = String(enabled);
      wrap.setAttribute('aria-pressed', String(enabled));
    });
  });

  const readingWidth = matchMedia('(max-width: 68rem)');
  document.querySelectorAll('details.wa-detail-toc').forEach(contents => {
    const sizeContents = () => { contents.open = !readingWidth.matches; };
    sizeContents();
    readingWidth.addEventListener('change', sizeContents);
  });

  const compact = matchMedia('(max-width: 56rem)');
  document.querySelectorAll('.wa-adaptive-table').forEach(table => {
    const toggle = table.querySelector('button[data-adaptive-toggle]');
    if (!toggle) return;
    const current = () => table.dataset.view || (compact.matches ? 'list' : 'table');
    const update = () => {
      toggle.textContent = current() === 'table' ? (toggle.dataset.labelList || 'Als beschriftete Einträge lesen') : (toggle.dataset.labelTable || 'Als Vergleichstabelle anzeigen');
    };
    toggle.addEventListener('click', () => {
      table.dataset.view = current() === 'table' ? 'list' : 'table';
      update();
    });
    compact.addEventListener('change', update);
    update();
  });
})();
